import React, { memo, useMemo } from 'react';
import { Separator, Text } from '@cuemath/web-comps';
import COLORS from 'constants/colors';
import { ArrowForward } from '@styled-icons/material/ArrowForward';
import { DETECTION_TYPES_CONFIG } from 'constants/av-review';
import { VERDICT_STATES, VERDICT_DISPLAY } from '../../constants';
import EventSummaryRow from '../event-summary-row';
import {
  SummaryHeader,
  DetectionVerdictBadge,
  NextButton,
  NextButtonText,
  TableHeaderRow,
} from './detection-summary-styled';
import {
  EventIndexCell,
  EventVerdictCell,
  ChunkCountCell,
  DurationCell,
} from '../event-summary-row/event-summary-row-styled';
import { FlexView } from '@cuemath/comps';

const DetectionSummary = ({ detection, nextDetectionType, onGoToNext, onReReviewEvent, isReviewSubmitted }) => {
  const events = detection?.events;
  const verdict = detection?.verdict;
  const typeConfig = DETECTION_TYPES_CONFIG[detection?.type] || {};
  const label = typeConfig.label || detection?.type;

  const verdictConfig = VERDICT_DISPLAY[verdict] || VERDICT_DISPLAY[VERDICT_STATES.PENDING];

  const { validCount, invalidCount } = useMemo(
    () => (events || []).reduce(
      (acc, e) => {
        if (e.is_valid === true) acc.validCount++;
        else if (e.is_valid === false) acc.invalidCount++;
        return acc;
      },
      { validCount: 0, invalidCount: 0 },
    ),
    [events],
  );

  const nextLabel = nextDetectionType
    ? DETECTION_TYPES_CONFIG[nextDetectionType]?.label || nextDetectionType
    : null;

  if (!detection) return null;

  return (
    <FlexView>
      <SummaryHeader flexDirection="row" alignItems="center" justifyContent="space-between">
        <FlexView flexDirection="row" alignItems="center">
          <DetectionVerdictBadge bg={verdictConfig.bgColor} flexDirection="row" alignItems="center" gap={6}>
            <Text variant="cta2" color={verdictConfig.color}>
              {verdictConfig.icon} {label} — {verdictConfig.label}
            </Text>
          </DetectionVerdictBadge>
          <FlexView gutter={8}>
            <Text variant="body3" color={COLORS.BLACK_T_60}>
              {validCount} valid &middot; {invalidCount} invalid
            </Text>
          </FlexView>
        </FlexView>
      <Text variant="body3" color={COLORS.BLACK_T_60}>
        {isReviewSubmitted ? 'Click any event to view evidence' : 'Click any event to re-review'}
      </Text>
      </SummaryHeader>
      <TableHeaderRow flexDirection="row" alignItems="center" gap={12} gutter={16}>
        <EventIndexCell><Text variant="eyebrow2" color={COLORS.BLACK_T_60}>#</Text></EventIndexCell>
        <EventVerdictCell><Text variant="eyebrow2" color={COLORS.BLACK_T_60}>Verdict</Text></EventVerdictCell>
        <ChunkCountCell><Text variant="eyebrow2" color={COLORS.BLACK_T_60}>Chunks</Text></ChunkCountCell>
        <DurationCell><Text variant="eyebrow2" color={COLORS.BLACK_T_60}>Duration</Text></DurationCell>
      </TableHeaderRow>
      {events.map((event, index) => (
        <EventSummaryRow key={event.id} event={event} index={index} onReReview={onReReviewEvent} isReviewSubmitted={isReviewSubmitted} />
      ))}

      {nextLabel && (
        <NextButton onClick={onGoToNext}>
          <NextButtonText variant="cta2" color={COLORS.WHITE}>
            Next <ArrowForward size={18} /> {nextLabel}
          </NextButtonText>
        </NextButton>
      )}
      <Separator height={32} />
    </FlexView>
  );
};

export default memo(DetectionSummary);
